import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineController,
  BarController,
} from 'chart.js';

// Chart.js requires you to register the components you want to use.
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineController,
  BarController
);

const DashboardPage = () => {
  const navigate = useNavigate();
  const [expenseData, setExpenseData] = useState([]);
  const [storeData, setStoreData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [discountData, setDiscountData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(30);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api';
      
      try {
        const [dailyRes, storeRes, discountRes, categoryRes] = await Promise.all([
          fetch(`${backendUrl}/dashboard/daily-expenses?days=${timeRange}`),
          fetch(`${backendUrl}/dashboard/expense-by-store?days=${timeRange}`),
          fetch(`${backendUrl}/dashboard/discount-by-store?days=${timeRange}`),
          fetch(`${backendUrl}/dashboard/expense-by-category?days=${timeRange}`)
        ]);

        if (!dailyRes.ok || !storeRes.ok || !discountRes.ok || !categoryRes.ok) {
          throw new Error('Failed to fetch dashboard data');
        }

        const dailyData = await dailyRes.json();
        const storeData = await storeRes.json();
        const discountData = await discountRes.json();
        const categoryData = await categoryRes.json();

        let processedData;
        if (timeRange > 0) {
            const expenseMap = new Map(dailyData.map(item => [item.date, parseFloat(item.total)]));
            const allDatesData = [];
            const today = new Date();
            for (let i = timeRange - 1; i >= 0; i--) {
                const date = new Date();
                date.setDate(today.getDate() - i);
                const dateString = date.toISOString().split('T')[0];
                allDatesData.push({ date: dateString, total: expenseMap.get(dateString) || 0 });
            }
            processedData = allDatesData;
        } else {
            processedData = dailyData;
        }

        let cumulativeTotal = 0;
        const finalData = processedData.map(item => {
            cumulativeTotal += item.total;
            return { ...item, cumulativeTotal: cumulativeTotal };
        });

        setExpenseData(finalData);
        setStoreData(storeData);
        setDiscountData(discountData);
        setCategoryData(categoryData);

      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        setError(err.message || 'Failed to fetch dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [timeRange]);

  const renderChart = () => {
    if (loading) {
      return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
    }

    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }

    if (!expenseData || expenseData.length === 0) {
      return <Typography>No expense data available for the selected range.</Typography>;
    }

    const chartData = {
      labels: expenseData.map(item => item.date),
      datasets: [
        {
          type: 'bar',
          label: 'Daily Expense (£)',
          data: expenseData.map(item => item.total),
          backgroundColor: 'rgba(136, 132, 216, 0.5)',
          borderColor: 'rgba(136, 132, 216, 1)',
          [isSmallScreen ? 'xAxisID' : 'yAxisID']: isSmallScreen ? 'x' : 'y',
        },
        {
          type: 'line',
          label: 'Cumulative Expense (£)',
          data: expenseData.map(item => item.cumulativeTotal),
          borderColor: 'rgba(255, 99, 132, 1)',
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
          tension: 0.1,
          [isSmallScreen ? 'xAxisID' : 'yAxisID']: isSmallScreen ? 'x1' : 'y1',
        },
      ],
    };

    const getAxisOptions = (position, title, drawGrid = true) => ({
      type: 'linear',
      display: true,
      position,
      beginAtZero: true,
      title: {
        display: true,
        text: title,
      },
      ticks: {
        callback: (value) => `£${value}`,
      },
      grid: {
        drawOnChartArea: drawGrid,
      },
    });

    const chartOptions = {
      indexAxis: isSmallScreen ? 'y' : 'x',
      responsive: true,
      maintainAspectRatio: false,
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const element = elements[0];
          if (element.datasetIndex === 0) { // Only handle clicks on the bar dataset
            const date = chartData.labels[element.index];
            navigate(`/history/${date}`);
          }
        }
      },
      plugins: {
        legend: {
          position: 'top',
        },
      },
      scales: isSmallScreen ? {
        x: getAxisOptions('top', 'Daily Expense (£)'),
        x1: getAxisOptions('bottom', 'Cumulative Expense (£)', false),
      } : {
        y: getAxisOptions('left', 'Daily Expense (£)'),
        y1: getAxisOptions('right', 'Cumulative Expense (£)', false),
      },
    };

    const chartHeight = isSmallScreen ? `${Math.max(400, expenseData.length * 25)}px` : '400px';

    return (
      <Box sx={{ height: chartHeight }}>
        <Bar key={isSmallScreen ? 'h' : 'v'} options={chartOptions} data={chartData} />
      </Box>
    );
  };

  const renderStorePieChart = () => {
    if (loading) return null; 
    if (error) return null;
    if (!storeData || storeData.length === 0) {
      return <Typography sx={{ textAlign: 'center', mt: 4 }}>No store data available.</Typography>;
    }

    const pieChartData = {
      labels: storeData.map(item => item.store_name),
      datasets: [
        {
          label: 'Expenses by Store',
          data: storeData.map(item => item.total),
          backgroundColor: [
            'rgba(255, 99, 132, 0.7)',
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 206, 86, 0.7)',
            'rgba(75, 192, 192, 0.7)',
            'rgba(153, 102, 255, 0.7)',
            'rgba(255, 159, 64, 0.7)',
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };

    const pieChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
        },
        title: {
          display: true,
          text: 'Expenses by Store',
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const label = context.label || '';
              const value = context.raw || 0;
              const allData = context.chart.data.datasets[0].data;
              const total = allData.reduce((acc, current) => acc + parseFloat(current), 0);
              const percentage = total > 0 ? ((parseFloat(value) / total) * 100).toFixed(2) + '%' : '0%';
              return `${label}: £${parseFloat(value).toFixed(2)} (${percentage})`;
            }
          }
        }
      },
    };

    return (
      <Box sx={{ height: '400px', p: 1 }}>
        <Doughnut data={pieChartData} options={pieChartOptions} />
      </Box>
    );
  };

  const renderCategoryPieChart = () => {
    if (loading) return null;
    if (error) return null;
    if (!categoryData || categoryData.length === 0) {
      return <Typography sx={{ textAlign: 'center', mt: 4 }}>No category data available.</Typography>;
    }

    const pieChartData = {
      labels: categoryData.map(item => item.category_name),
      datasets: [
        {
          label: 'Expenses by Category (£)',
          data: categoryData.map(item => item.total),
          backgroundColor: [
            'rgba(255, 99, 132, 0.7)',
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 206, 86, 0.7)',
            'rgba(75, 192, 192, 0.7)',
            'rgba(153, 102, 255, 0.7)',
            'rgba(255, 159, 64, 0.7)',
            'rgba(199, 199, 199, 0.7)',
            'rgba(83, 102, 255, 0.7)',
            'rgba(100, 255, 100, 0.7)',
          ],
          borderColor: '#fff',
          borderWidth: 1,
        },
      ],
    };

    const pieChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
        },
        title: {
          display: true,
          text: 'Expenses by Category',
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const label = context.label || '';
              const value = context.raw || 0;
              const allData = context.chart.data.datasets[0].data;
              const total = allData.reduce((acc, current) => acc + parseFloat(current), 0);
              const percentage = total > 0 ? ((parseFloat(value) / total) * 100).toFixed(2) + '%' : '0%';
              return `${label}: £${parseFloat(value).toFixed(2)} (${percentage})`;
            }
          }
        }
      },
    };

    return (
      <Box sx={{ height: '400px', p: 1 }}>
        <Doughnut data={pieChartData} options={pieChartOptions} />
      </Box>
    );
  };

  const renderDiscountPieChart = () => {
    if (loading) return null;
    if (error) return null;
    if (!discountData || discountData.length === 0) {
      return <Typography sx={{ textAlign: 'center', mt: 4 }}>No discount data available.</Typography>;
    }

    const pieChartData = {
      labels: discountData.map(item => item.store_name),
      datasets: [
        {
          label: 'Discounts by Store (£)',
          data: discountData.map(item => item.total_discount),
          backgroundColor: [
            'rgba(75, 192, 192, 0.7)',
            'rgba(255, 159, 64, 0.7)',
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 206, 86, 0.7)',
            'rgba(153, 102, 255, 0.7)',
            'rgba(255, 99, 132, 0.7)',
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(255, 159, 64, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 99, 132, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };

    const pieChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
        },
        title: {
          display: true,
          text: 'Discounts by Store',
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const label = context.label || '';
              const value = context.raw || 0;
              const allData = context.chart.data.datasets[0].data;
              const total = allData.reduce((acc, current) => acc + parseFloat(current), 0);
              const percentage = total > 0 ? ((parseFloat(value) / total) * 100).toFixed(2) + '%' : '0%';
              return `${label}: £${parseFloat(value).toFixed(2)} (${percentage})`;
            }
          }
        }
      },
    };

    return (
      <Box sx={{ height: '400px', p: 1 }}>
        <Doughnut data={pieChartData} options={pieChartOptions} />
      </Box>
    );
  };

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 2, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            Expense Overview
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={timeRange}
              label="Date Range"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value={7}>Last 7 Days</MenuItem>
              <MenuItem value={30}>Last 30 Days</MenuItem>
              <MenuItem value={90}>Last 90 Days</MenuItem>
              <MenuItem value={0}>All History</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {renderChart()}
      </Paper>

      <Paper elevation={3} sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Expenses by Store
        </Typography>
        {renderStorePieChart()}
      </Paper>

      <Paper elevation={3} sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Expenses by Category
        </Typography>
        {renderCategoryPieChart()}
      </Paper>

      <Paper elevation={3} sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Discounts by Store
        </Typography>
        {renderDiscountPieChart()}
      </Paper>
    </Box>
  );
};

export default DashboardPage;